import { useRevealAll } from '../hooks/useReveal';

interface Feature {
  icon: string;
  title: string;
  desc: string;
  tags: { label: string; variant?: 'gold' | 'green' }[];
  variant?: 'gold' | 'green';
  iconBg?: string;
}

const FEATURES: Feature[] = [
  {
    icon: '\uD83D\uDCF9',
    title: 'Virtual Consultations',
    desc: 'HD video and audio calls with WebRTC. Waiting room, screen sharing, consultation recording, and integrated clinical notes.',
    tags: [{ label: 'Video' }, { label: 'Audio' }, { label: 'Chat' }],
  },
  {
    icon: '\uD83E\uDD16',
    title: 'Heka AI Assistant',
    desc: 'AI-powered SOAP note generation, differential diagnosis support, drug interaction checking, and personalized patient health tips.',
    tags: [{ label: 'Clinical AI', variant: 'gold' }, { label: 'Patient Tips', variant: 'gold' }],
    variant: 'gold',
    iconBg: '#fef3c7',
  },
  {
    icon: '\uD83C\uDFE5',
    title: 'NHIS Integration',
    desc: 'Real-time membership verification, automated claim generation, batch claim submission, and reimbursement tracking for all providers.',
    tags: [{ label: 'Verify', variant: 'green' }, { label: 'Claims', variant: 'green' }, { label: 'Billing', variant: 'green' }],
    variant: 'green',
    iconBg: '#dcfce7',
  },
  {
    icon: '\uD83D\uDDFA',
    title: 'Pharmacy Network',
    desc: 'Find nearest pharmacies on an interactive map. Order OTC medications or prescription fulfillment with real-time delivery tracking.',
    tags: [{ label: 'Geolocation' }, { label: 'OTC Orders' }, { label: 'Delivery' }],
  },
  {
    icon: '\uD83C\uDFE0',
    title: 'Home Nursing Services',
    desc: 'Book qualified nurses for bed baths, wound care, medication administration, IV therapy, post-operative care, and elderly support \u2014 at home.',
    tags: [{ label: 'Home Visits' }, { label: 'GPS Tracking' }],
  },
  {
    icon: '\uD83D\uDCCA',
    title: 'Health Tracking',
    desc: 'Log vitals (BP, glucose, weight, O2), track medication compliance with streaks and reminders, and visualize trends with interactive charts.',
    tags: [{ label: 'Vitals' }, { label: 'Meds' }, { label: 'Trends' }],
  },
  {
    icon: '\uD83D\uDCCB',
    title: 'Digital Clinical Records',
    desc: 'AI-integrated SOAP notes, structured diagnoses with ICD-10, care plans, lab results, and full EHR capabilities for every consultation.',
    tags: [{ label: 'SOAP Notes' }, { label: 'ICD-10' }, { label: 'Care Plans' }],
  },
  {
    icon: '\uD83D\uDCB3',
    title: 'Mobile Money Billing',
    desc: 'Pay with MTN MoMo, Vodafone Cash, or AirtelTigo via Hubtel. Instant payment confirmation, automated receipts, and refund management.',
    tags: [{ label: 'MTN MoMo' }, { label: 'Hubtel' }, { label: 'Instant' }],
  },
  {
    icon: '\uD83D\uDCE1',
    title: 'Works Offline',
    desc: 'Progressive Web App with offline capabilities. Log vitals, view medications, and check appointments even without internet. Data syncs on reconnection.',
    tags: [{ label: 'PWA' }, { label: 'Offline First' }],
  },
];

function tagStyle(variant?: 'gold' | 'green') {
  if (variant === 'gold') return { background: '#fef3c7', color: '#92400e' };
  if (variant === 'green') return { background: '#dcfce7', color: '#15803d' };
  return {};
}

export default function FeaturesGrid() {
  const containerRef = useRevealAll();

  return (
    <section className="section section-alt" id="features-grid" ref={containerRef}>
      <div className="section-header reveal">
        <div className="section-tag">Platform Features</div>
        <h2 className="section-title">Everything healthcare, all in one place</h2>
        <p className="section-subtitle">
          Purpose-built for Ghana with the features that actually matter to patients and providers.
        </p>
      </div>

      <div className="features-grid">
        {FEATURES.map((f) => (
          <div
            className={`feature-card reveal${f.variant ? ` ${f.variant}` : ''}`}
            key={f.title}
          >
            <div className="feature-icon" style={f.iconBg ? { background: f.iconBg } : undefined}>
              {f.icon}
            </div>
            <div className="feature-title">{f.title}</div>
            <div className="feature-desc">{f.desc}</div>
            <div className="feature-tags">
              {f.tags.map((t) => (
                <span className="feature-tag" key={t.label} style={tagStyle(t.variant)}>
                  {t.label}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
